import { useCallback, useEffect, useState } from 'react';

export interface CommandPaletteController {
    open: boolean;
    openPalette: () => void;
    closePalette: () => void;
}

/**
 * EAA-1813: owns the ⌘K palette's open state plus the global hotkey.
 *
 * The state cannot live inside `CommandPalette` itself: the palette is only
 * mounted while open (so query, debounce and active-index state reset cleanly on
 * every open), and the topbar's search box is a second way to open it. Both
 * topbars — `AppBar` (launcher/profile) and `ConsoleTopbar` (console shell) —
 * call this hook, which is also why the listener lives here rather than being
 * duplicated in each.
 */
export function useCommandPalette(): CommandPaletteController {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Accept both the mac (⌘) and windows/linux (Ctrl) chord. `event.key`
            // is compared case-insensitively because ⌘K arrives as "k" but
            // Shift-holding users send "K".
            const isPaletteChord =
                (event.metaKey || event.ctrlKey) &&
                !event.altKey &&
                event.key.toLowerCase() === 'k';
            if (!isPaletteChord) return;
            // Firefox and Chrome both bind Ctrl/⌘+K to the address bar's search
            // field, so this only works if the browser default is suppressed.
            event.preventDefault();
            setOpen(true);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const openPalette = useCallback(() => setOpen(true), []);
    const closePalette = useCallback(() => setOpen(false), []);

    return { open, openPalette, closePalette };
}
